import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, LogIn } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';

import { CDN_URL } from '@/config/cdn';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Branded wrapper that matches CustomerLogin's look-and-feel: dojo
 * background, glowing orbs, logo with golden ring, and Owaza colors.
 *
 * Centralized here so all four states (ready / verifying / success /
 * error) share the same identity \u2014 a member arriving here from the
 * invite email should instantly feel they're in the right place.
 */
const BrandedShell = ({ header, children }) => (
  <div className="member-login-bg relative flex items-center justify-center px-4">
    <div className="login-orb login-orb-1"></div>
    <div className="login-orb login-orb-2"></div>
    <div className="login-orb login-orb-3"></div>

    <Card className="member-login-card w-full max-w-md relative z-10 dojo-animate">
      <CardHeader className="text-center pb-2">
        <div className="flex justify-center mb-4">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-[#C9A227]/40 blur-lg animate-pulse scale-110"></div>
            <img
              src={`${CDN_URL}/images/owaza-logo.jpg`}
              alt="Owaza Martial Arts"
              className="relative h-24 w-24 object-cover rounded-full shadow-2xl ring-4 ring-[#C9A227]/40"
            />
          </div>
        </div>
        {header}
      </CardHeader>
      <CardContent className="pt-4 text-center pb-8">{children}</CardContent>
    </Card>
  </div>
);

const MagicLinkVerify = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('ready'); // ready, verifying, success, error
  const [error, setError] = useState('');

  const token = searchParams.get('token');

  // Verify token on button click (not automatically on mount)
  // This prevents mail client previews from consuming the token
  const handleVerify = async () => {
    if (!token) {
      setStatus('error');
      setError('Invalid link. Please request a new sign-in link.');
      return;
    }

    setStatus('verifying');

    try {
      // Use unified verify endpoint
      const response = await fetch(`${API_BASE}/api/member/verify-magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });

      if (response.ok) {
        const data = await response.json();
        const authType = data.auth_type;

        if (authType === 'student') {
          // Store member auth data for student
          localStorage.setItem('memberToken', data.token);
          localStorage.setItem('memberData', JSON.stringify({
            auth_type: data.auth_type,
            entity_id: data.entity_id,
            entity: data.entity,
            permissions: data.permissions
          }));
          // Clear any old customer data
          localStorage.removeItem('customerToken');
          localStorage.removeItem('customerData');
        } else {
          // Store customer auth data (legacy + new format)
          localStorage.setItem('customerToken', data.token);
          localStorage.setItem('customerData', JSON.stringify(data.customer));
          // Also store as member data for unified handling
          localStorage.setItem('memberToken', data.token);
          localStorage.setItem('memberData', JSON.stringify({
            auth_type: 'customer',
            entity_id: data.customer?.id,
            entity: data.customer,
            permissions: { can_view_billing: true, can_view_siblings: true }
          }));
        }

        setStatus('success');

        // Check for stored redirect path (from resource link flow)
        const storedRedirect = sessionStorage.getItem('loginRedirect');
        sessionStorage.removeItem('loginRedirect');

        // Redirect to stored path or dashboard after brief success message
        setTimeout(() => {
          navigate(storedRedirect || '/');
        }, 1500);
      } else {
        const errorData = await response.json();
        setStatus('error');
        setError(errorData.detail || 'This link is invalid or has expired.');
      }
    } catch (err) {
      setStatus('error');
      setError('Something went wrong. Please try again.');
    }
  };

  // Show error immediately if no token present
  if (!token && status === 'ready') {
    return (
      <BrandedShell
        header={
          <>
            <CardTitle className="text-3xl font-outfit font-bold text-stone-800 dark:text-stone-100">
              Invalid Link
            </CardTitle>
            <CardDescription className="text-stone-600 dark:text-stone-400 font-dm-sans mt-2">
              This link is missing or invalid.
            </CardDescription>
          </>
        }
      >
        <div className="w-14 h-14 bg-red-100 dark:bg-red-900/40 rounded-full flex items-center justify-center mx-auto mb-4">
          <XCircle className="h-7 w-7 text-red-600 dark:text-red-400" />
        </div>
        <p className="text-stone-600 dark:text-stone-400 font-dm-sans mb-5">
          Please request a new sign-in link from the login page.
        </p>
        <Button
          onClick={() => navigate('/login')}
          className="w-full bg-[#9B1B30] hover:bg-[#7d1626] text-white font-dm-sans"
        >
          Request New Link
        </Button>
      </BrandedShell>
    );
  }

  return (
    <BrandedShell
      header={
        status === 'ready' ? (
            <>
              <CardTitle className="text-3xl font-outfit font-bold text-stone-800 dark:text-stone-100">
                Welcome to Owaza
              </CardTitle>
              <CardDescription className="text-stone-600 dark:text-stone-400 font-dm-sans mt-2">
                You're one tap away from your Member Portal.
              </CardDescription>
            </>
          ) : status === 'verifying' ? (
            <>
              <CardTitle className="text-3xl font-outfit font-bold text-stone-800 dark:text-stone-100">
                Signing You In
              </CardTitle>
              <CardDescription className="text-stone-600 dark:text-stone-400 font-dm-sans mt-2">
                One moment while we verify your link.
              </CardDescription>
            </>
          ) : status === 'success' ? (
            <>
              <CardTitle className="text-3xl font-outfit font-bold text-stone-800 dark:text-stone-100">
                You're In
              </CardTitle>
              <CardDescription className="text-stone-600 dark:text-stone-400 font-dm-sans mt-2">
                OSU! Taking you to your portal now.
              </CardDescription>
            </>
        ) : (
          <>
            <CardTitle className="text-3xl font-outfit font-bold text-stone-800 dark:text-stone-100">
              Sign In Failed
            </CardTitle>
            <CardDescription className="text-stone-600 dark:text-stone-400 font-dm-sans mt-2">
              Let's get you a fresh link.
            </CardDescription>
          </>
        )
      }
    >
      {status === 'ready' && (
              <>
                <div className="w-14 h-14 bg-[#9B1B30]/10 dark:bg-[#9B1B30]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <LogIn className="h-7 w-7 text-[#9B1B30] dark:text-[#C9A227]" />
                </div>
                <p className="text-stone-600 dark:text-stone-400 font-dm-sans mb-6">
                  Tap below to confirm your sign in and head to your Member Portal.
                </p>
                <Button
                  onClick={handleVerify}
                  className="w-full bg-[#9B1B30] hover:bg-[#7d1626] text-white font-dm-sans"
                >
                  Confirm Sign In
                </Button>
              </>
            )}

            {status === 'verifying' && (
              <>
                <div className="w-14 h-14 bg-stone-100 dark:bg-stone-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Loader2 className="h-7 w-7 text-[#9B1B30] dark:text-[#C9A227] animate-spin" />
                </div>
                <p className="text-stone-600 dark:text-stone-400 font-dm-sans">
                  Hang tight \u2014 this only takes a second.
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-14 h-14 bg-emerald-100 dark:bg-emerald-900/40 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
                </div>
                <p className="text-stone-600 dark:text-stone-400 font-dm-sans">
                  Redirecting to your dashboard\u2026
                </p>
              </>
            )}

      {status === 'error' && (
        <>
          <div className="w-14 h-14 bg-red-100 dark:bg-red-900/40 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="h-7 w-7 text-red-600 dark:text-red-400" />
          </div>
          <p className="text-stone-600 dark:text-stone-400 font-dm-sans mb-5">
            {error}
          </p>
          <Button
            onClick={() => navigate('/login')}
            className="w-full bg-[#9B1B30] hover:bg-[#7d1626] text-white font-dm-sans"
          >
            Request New Link
          </Button>
        </>
      )}
    </BrandedShell>
  );
};

export default MagicLinkVerify;
